import React from "react";
import { Alert, Button, Modal, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import APIService from "../../Service/APIService";
import { canAttachDpa } from "./accountManufacturerDpaAccess";

export default function AttachDpaModal({
  row,
  account,
  user,
  isReady,
  readOnly,
  onClose,
  onRefresh,
}) {
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [completed, setCompleted] = React.useState(false);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const mounted = React.useRef(true);
  const submitting = React.useRef(false);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const eligible = canAttachDpa({ user, isReady, readOnly, row, account });

  const refresh = async () => {
    if (!onRefresh) throw new Error("Refresh unavailable");
    await onRefresh();
  };
  const submit = async () => {
    if (
      submitting.current ||
      !eligible ||
      !file ||
      refreshRequired ||
      completed
    )
      return;
    submitting.current = true;
    setBusy(true);
    setError("");
    let attached = false;
    try {
      const form = new FormData();
      form.append(
        "data",
        JSON.stringify({ AccountID: row.AccountID, ManufacturerID: row.ID }),
      );
      form.append("file", file);
      await new APIService().addFileDataToBackend(
        form,
        "attachaccountmanufacturerdpa",
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      attached = true;
      if (!mounted.current) return;
      setCompleted(true);
      await refresh();
      if (mounted.current) {
        message.success("DPA attached.");
        onClose();
      }
    } catch (failure) {
      if (!mounted.current) return;
      if (attached) {
        setError(
          "DPA attached. The table could not be refreshed. Refresh to see the document.",
        );
        setRefreshRequired(true);
      } else {
        const status = failure?.response?.status;
        const uncertain = !status || status >= 500;
        const needsRefresh = uncertain || [401, 403, 404, 409].includes(status);
        setRefreshRequired(needsRefresh);
        setError(
          uncertain
            ? "Attachment could not be confirmed. Refresh the table before trying again."
            : failure?.response?.data?.error ||
                failure?.response?.data?.message ||
                "Unable to attach DPA.",
        );
        if (needsRefresh) {
          try {
            await refresh();
          } catch {
            /* explicit refresh action remains available */
          }
        }
      }
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const retryRefresh = async () => {
    setBusy(true);
    try {
      await refresh();
      if (mounted.current) onClose();
    } catch {
      if (mounted.current)
        setError(
          completed
            ? "DPA attached. Refresh the account to see the document."
            : "Unable to refresh. Reload the account before trying again.",
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`Attach DPA for ${row.ManufacturerName}`}
      onCancel={() => !busy && onClose()}
      closable={!busy}
      maskClosable={!busy}
      keyboard={!busy}
      footer={[
        <Button key="cancel" disabled={busy} onClick={onClose}>
          Close
        </Button>,
        refreshRequired ? (
          <Button
            key="refresh"
            type="primary"
            loading={busy}
            onClick={retryRefresh}
          >
            Refresh table
          </Button>
        ) : (
          <Button
            key="attach"
            type="primary"
            loading={busy}
            disabled={!file || !eligible || completed}
            onClick={submit}
          >
            Attach DPA
          </Button>
        ),
      ]}
    >
      <p>{account.AccountName}</p>
      <p>Attach the signed DPA. Approval and pricing will remain unchanged.</p>
      <p id="dpa-upload-help">PDF only, up to 4 MiB.</p>
      {error && (
        <Alert
          role="alert"
          type={completed ? "warning" : "error"}
          message={error}
          showIcon
        />
      )}
      {completed && !error && (
        <Alert role="status" type="success" message="DPA attached." />
      )}
      <Upload
        accept=".pdf,application/pdf"
        maxCount={1}
        disabled={busy || refreshRequired || completed}
        fileList={file ? [file] : []}
        onRemove={() => {
          setFile(null);
          setError("");
        }}
        beforeUpload={(selected) => {
          if (
            !/\.pdf$/i.test(selected.name) ||
            selected.type !== "application/pdf" ||
            !selected.size ||
            selected.size > 4 * 1024 * 1024
          ) {
            setError("Choose a nonempty PDF file up to 4 MiB.");
            return Upload.LIST_IGNORE;
          }
          setFile(selected);
          setError("");
          return false;
        }}
      >
        <Button
          icon={<UploadOutlined />}
          disabled={busy || refreshRequired || completed}
          aria-describedby="dpa-upload-help"
        >
          Choose DPA PDF
        </Button>
      </Upload>
    </Modal>
  );
}
